import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export type RagLightboxItem = {
  src: string;
  label?: string;
};

/** Fullscreen image viewer — header, main image, caption, thumbnail strip. */
export function RagImageLightbox({
  src,
  gallery,
  title,
  onClose,
}: {
  src: string | null;
  gallery?: RagLightboxItem[];
  /** Optional header title, e.g. "Step 1 · Collect the components". */
  title?: string;
  onClose: () => void;
}) {
  const items = useMemo(
    () => (gallery?.length ? gallery : src ? [{ src, label: undefined }] : []),
    [gallery, src]
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!src || !items.length) return;
    const nextIndex = items.findIndex((item) => item.src === src);
    setIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [src, items]);

  const active = items[index];
  const hasMultiple = items.length > 1;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!src) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) goPrev();
      if (e.key === "ArrowRight" && hasMultiple) goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [src, onClose, hasMultiple, goPrev, goNext]);

  if (!src || !active) return null;

  const headerTitle = title || active.label;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 2,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#0b111b",
      }}
    >
      {/* Header */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: { xs: 1.5, md: 2.5 },
          py: 1.5,
          minHeight: 56,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{
              color: "common.white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {headerTitle && (
            <Typography
              noWrap
              sx={{
                color: "common.white",
                fontWeight: 600,
                fontSize: { xs: 14, md: 15 },
                lineHeight: 1.3,
              }}
            >
              {headerTitle}
            </Typography>
          )}
        </Box>

        {hasMultiple && (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.85)",
              fontWeight: 600,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {index + 1} / {items.length}
          </Typography>
        )}
      </Box>

      {/* Main image — fill available viewport, keep aspect ratio */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1.5, md: 3 },
          py: 0.5,
          width: "100%",
        }}
      >
        <Box
          component="img"
          src={active.src}
          alt={active.label ?? "Diagram"}
          sx={{
            width: "100%",
            height: "100%",
            maxWidth: "94vw",
            maxHeight: hasMultiple ? "calc(100vh - 180px)" : "calc(100vh - 88px)",
            objectFit: "contain",
            objectPosition: "center",
            borderRadius: 1.5,
            userSelect: "none",
          }}
        />
      </Box>

      {/* Caption + thumbnails */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          pb: { xs: 2.5, md: 3 },
          pt: 1,
        }}
      >
        {hasMultiple && (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              maxWidth: "100%",
              overflowX: "auto",
              py: 0.5,
              px: 0.5,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(255,255,255,0.25)",
                borderRadius: 999,
              },
            }}
            role="tablist"
            aria-label="Gallery thumbnails"
          >
            {items.map((item, i) => {
              const selected = i === index;
              return (
                <Box
                  key={`${item.src}-${i}`}
                  component="button"
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-label={item.label ?? `Image ${i + 1}`}
                  onClick={() => setIndex(i)}
                  sx={{
                    p: 0,
                    width: 64,
                    height: 64,
                    minWidth: 64,
                    borderRadius: 1.25,
                    overflow: "hidden",
                    border: "2.5px solid",
                    borderColor: selected ? "common.white" : "transparent",
                    opacity: selected ? 1 : 0.7,
                    cursor: "pointer",
                    bgcolor: "rgba(255,255,255,0.08)",
                    flexShrink: 0,
                    transition: "opacity 0.15s ease, border-color 0.15s ease",
                    "&:hover": { opacity: 1 },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "common.white",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.src}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
